import { sectionRenderer } from '@theme/section-renderer';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { Component } from '@theme/component';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, startViewTransition } from '@theme/utilities';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';
/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    newParameters.delete('page');

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    // Preserve a committed model search (?model=, the trade-in "Search by Model" term)
    // across filter updates — it isn't a form field, so rebuilding the URL from the form
    // alone would silently drop it.
    const model = new URL(window.location.href).searchParams.get('model');
    if (model) newParameters.set('model', model);

    return newParameters;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section
   */
  updateFilters = () => {
    // Desktop with an active Search-by-Model term (?model=): reload to the new filter URL rather
    // than morphing in place. The client-side search overlay (chip + client-side card filtering +
    // all-pages loader) and the AJAX morph otherwise desync (stuck spinner, stale chip, matches
    // beyond page 1). A reload re-renders server-side and re-applies the search cleanly. Only when
    // a search is active — plain filtering stays a smooth in-place morph; mobile is untouched.
    if (this.#shouldReloadForSearch()) {
      this.#navigateToCurrentFilters();
      return;
    }

    // Otherwise (no active search, or the mobile drawer) apply filters live: push the new URL and
    // re-render the section in place, so the count, chips and results update as filters are
    // selected. The mobile drawer stays open until its "See items" CTA is tapped (see
    // applyFilters) — the in-place morph preserves the open modal drawer (see morph.js).
    this.#updateURLHash();
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  };

  /**
   * Whether a filter change should reload the page instead of morphing in place. True only on the
   * desktop layout while a Search-by-Model term is committed in the URL. The mobile drawer and
   * mobile sort bar are excluded so their live, drawer-stays-open behaviour is kept.
   *
   * Uses matchMedia with the same 750px breakpoint the CSS uses (not window.innerWidth, which
   * includes the scrollbar) so JS and CSS agree by definition — otherwise a ~15px band near the
   * breakpoint could reload out of an open mobile drawer while CSS still shows the mobile layout.
   * @returns {boolean}
   */
  #shouldReloadForSearch() {
    return (
      window.matchMedia('(min-width: 750px)').matches && new URL(window.location.href).searchParams.has('model')
    );
  }

  /**
   * Full-page navigate to the URL for the currently-selected filters (preserving the committed
   * ?model= search, which createURLParameters carries over). Used by the desktop reload-on-search
   * path so filters + search re-render server-side together.
   */
  #navigateToCurrentFilters() {
    const url = new URL(window.location.href);
    url.search = '';
    for (const [key, value] of this.createURLParameters().entries()) {
      url.searchParams.append(key, value);
    }
    window.location.assign(url.toString());
  }

  /**
   * The mobile drawer's "See items" CTA. Filters now apply live as each is selected (the URL
   * and results update on every change), so this simply closes the drawer. Exception: an
   * un-committed "Search by Model" change should still apply on "See items" — a term typed but not
   * committed with Enter, or an emptied box that should clear an active search. Either commits as a
   * navigation, which also carries the already-applied filters.
   */
  applyFilters = () => {
    const searchInput = /** @type {HTMLInputElement|null} */ (
      document.getElementById('gc-ti-search-input-mobile') ?? document.getElementById('gc-ti-search-input')
    );
    const typedTerm = searchInput?.value.trim() ?? '';
    const committedModel = new URL(window.location.href).searchParams.get('model') ?? '';

    // A pending "Search by Model" change — commit it via a real navigation so it applies when the
    // drawer closes. "Pending" is any divergence from the committed ?model=: a newly typed term to
    // apply, OR an emptied box that should clear an active search (typedTerm '' while ?model= is
    // still set). The current filters are already in the URL.
    if (typedTerm !== committedModel) {
      const url = new URL(window.location.href);
      if (typedTerm) url.searchParams.set('model', typedTerm);
      else url.searchParams.delete('model');
      window.location.assign(url.toString());
      return;
    }

    // Nothing pending — the filters are already applied, so the CTA just closes the drawer.
    const dialog = /** @type {any} */ (this.closest('dialog-component'));
    if (dialog && typeof dialog.closeDialog === 'function') dialog.closeDialog();
  };

  /**
   * The most recent render request. Only its completion clears the busy state, so a stale
   * in-flight render can't hide the spinner while a newer one is still loading.
   * @type {number}
   */
  #renderToken = 0;

  /**
   * Toggles a busy/loading state on the section's results list. CSS dims the grid and shows
   * a spinner (and the drawer's "See items" button reflects it) while a re-render is in
   * flight, so filtering feels responsive instead of frozen — especially on mobile.
   * @param {boolean} isBusy
   */
  #setResultsBusy(isBusy) {
    const resultsList = document
      .getElementById(`shopify-section-${this.sectionId}`)
      ?.querySelector('results-list');
    if (resultsList) {
      if (isBusy) resultsList.setAttribute('aria-busy', 'true');
      else resultsList.removeAttribute('aria-busy');
    }

// Mobile filter drawer: the grid's busy spinner (above) sits BEHIND the open drawer, so also flag the
// drawer itself. CSS then dims the filter list and overlays a loading spinner to make the in-flight
// render visible in the drawer.
// Interactions are not blocked; rapid multi-taps are handled by debouncing the drawer render.
// Only the drawer's own facets form has a <dialog> ancestor, so the desktop sidebar is unaffected.
    const drawer = this.closest('dialog');
    if (drawer) {
      if (isBusy) drawer.setAttribute('data-facets-busy', 'true');
      else drawer.removeAttribute('data-facets-busy');
    }
  }

  /**
   * Coalesces rapid drawer taps into a SINGLE re-render. Each tap has already pushed its selection
   * to the URL (updateFilters → #updateURLHash) and updated the chips/badges, so the debounced
   * render — which reads the current URL — always reflects the LATEST selection set. This stops
   * rapid multi-tapping from firing a fetch/morph per tap (the "JS loop" that desynced the drawer
   * count/results — UAT) while every selection still registers and nothing gets locked out.
   * @type {() => void}
   */
  #debouncedDrawerRender = debounce(() => this.#renderDrawerSection(), 250);

  /**
   * Updates the section. The mobile drawer render is debounced (see above) so a burst of taps
   * produces one render; desktop renders immediately inside a view transition.
   */
  #updateSection() {
    // The mobile drawer is an open, top-layer modal <dialog>. Morphing the whole section down
    // through the open modal desyncs on repeat selections — the count/availability/results stop
    // updating until a reload (UAT; the reason the drawer previously used a full navigation). So
    // for the drawer we fetch the section HTML and apply targeted, structurally-aligned morphs to
    // just the pieces that change (see #syncFromHTML), which are reliable. Desktop keeps the
    // standard whole-section morph inside a view transition.
    if (this.closest('dialog')) {
      // Invalidate any in-flight render NOW, on every change — even though the fetch itself is
      // debounced. The render token must advance the instant the shopper changes a filter;
      // otherwise a slower earlier render can resolve during the debounce window and, since its
      // token still looks current, paint STALE chips over the newer checkbox state (the morph
      // preserves the box the shopper just ticked) → chips/checkboxes desync (UAT: "Set" chip
      // showing while "Single" is ticked). #renderDrawerSection then reads this same token.
      this.#renderToken++;
      // Show the spinner immediately for feedback; coalesce the actual fetch/morph so a burst of
      // taps produces one render against the latest URL instead of one (desyncing) render per tap.
      this.#setResultsBusy(true);
      this.#debouncedDrawerRender();
      return;
    }

    const token = ++this.#renderToken;
    this.#setResultsBusy(true);
    startViewTransition(
      () =>
        sectionRenderer.renderSection(this.sectionId).finally(() => {
          // Only the latest render clears the spinner (guards against a superseded render
          // resolving after a newer one and hiding the spinner mid-load).
          if (token === this.#renderToken) this.#setResultsBusy(false);
        }),
      ['product-grid']
    );
  }

  /**
   * Fetches the section for the current filter URL and applies the targeted drawer morphs. Runs
   * debounced via #debouncedDrawerRender so a burst of taps produces a single, reliable render.
   */
  #renderDrawerSection() {
    // Use the token as last advanced by #updateSection on the most recent change (do NOT bump
    // again here) — so a fetch started for an earlier selection is recognised as stale the moment
    // a newer change comes in, and its result is discarded instead of overwriting the newer state.
    const token = this.#renderToken;

    // getSectionHTML caches per full URL (filters included), so each distinct selection is fetched
    // fresh and re-selecting a combination is served from cache — correct without a cache-buster.
    sectionRenderer
      .getSectionHTML(this.sectionId, !Shopify.designMode)
      .then((html) => {
        if (html && token === this.#renderToken) this.#syncFromHTML(html);
      })
      .finally(() => {
        if (token === this.#renderToken) this.#setResultsBusy(false);
      });
  }

  /**
   * Applies freshly-rendered section HTML via targeted morphs of only the pieces that change — the
   * product grid, and (for the open mobile drawer) its count/active-chips/"See items" CTA/filter
   * list (with per-value availability). Each is a small, structurally-identical subtree, so the
   * morph is reliable — unlike morphing the whole section down through the open modal <dialog>,
   * which desyncs on repeat selections (count/results stop updating until a reload — UAT). Morph
   * preserves accordion open state and the checked/focused input the shopper is interacting with.
   * @param {string} html - The rendered section HTML.
   */
  #syncFromHTML(html) {
    const section = document.getElementById(`shopify-section-${this.sectionId}`);
    const newSection = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById(`shopify-section-${this.sectionId}`);
    if (!section || !newSection) return;

    /**
     * @param {ParentNode} root
     * @param {ParentNode} newRoot
     * @param {string} selector
     */
    const morphPair = (root, newRoot, selector) => {
      const target = root.querySelector(selector);
      const replacement = newRoot.querySelector(selector);
      if (!target || !replacement) return;
      try {
        morph(target, replacement, MORPH_OPTIONS);
      } catch (error) {
        // morph applies attributes via setAttribute, which WebKit (iOS) rejects with "Invalid
        // qualified name" for an invalid attribute name in malformed rendered HTML (e.g. an unescaped
        // inch-mark " that broke out of a double-quoted attribute). Blink tolerates it, which is why
        // filtering worked in desktop Chrome but threw on iOS and aborted the whole sync.
        //
        // Recovery is deliberately narrow: a partial morph may have MOVED nodes out of `replacement`,
        // so never reuse it — re-parse fresh. And never wholesale-replace a subtree that wraps the
        // drawer <dialog> or this component (results-list does) — that would detach the live form and
        // close/lose the open drawer. So recover ONLY the product grid (which contains neither),
        // re-parsed clean; for any other piece leave it unchanged. The count / "See items" text is
        // set separately below via textContent, so the critical signal still refreshes regardless.
        const grid = target.querySelector && target.querySelector('.product-grid');
        if (grid) {
          const freshGrid = new DOMParser().parseFromString(html, 'text/html').querySelector('.product-grid');
          if (freshGrid) {
            try {
              grid.replaceWith(freshGrid);
            } catch (swapError) {
              /* leave stale; the drawer count/filters still update below */
            }
          }
        }
      }
    };

    // The product grid (behind the drawer on mobile).
    morphPair(section, newSection, 'results-list');

    // The mobile sort bar is a SEPARATE form outside the drawer that mirrors the active filters as
    // hidden inputs, so sorting can carry them (it rebuilds the URL from its own FormData and wipes
    // the query string). The targeted drawer morphs below don't reach it, so refresh its mirror
    // here — otherwise a filter applied live in the drawer isn't reflected and the next sort drops
    // every applied filter (UAT: filters not maintained when sorting on tablet/mobile).
    morphPair(section, newSection, '.facets-mobile-sort__active-filters');

    // The open drawer's dynamic pieces — scoped to the dialog so the desktop copies aren't matched.
    const dialog = this.closest('dialog');
    const newDialog = newSection.querySelector('dialog.facets--drawer');
    if (!dialog || !newDialog) return;

    // The count and "See items (N)" number are the critical "don't filter yourself out" signal —
    // set their text directly (guaranteed to update, even if a morph inside the open modal ever
    // misbehaves) rather than relying solely on a morph.
    for (const selector of ['.facets-drawer__count', '.facets__see-results']) {
      const target = dialog.querySelector(selector);
      const replacement = newDialog.querySelector(selector);
      if (target && replacement) target.textContent = replacement.textContent;
    }

    // Active-count badge, applied-filter chips, and the filter list (per-value availability) —
    // morph to preserve accordion open state and the focused/checked input being interacted with.
    for (const selector of ['.facets-drawer__title', '.facets-remove', '.facets__filters-wrapper']) {
      morphPair(dialog, newDialog, selector);
    }
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url, dropModel = false) {
    // Shopify's url_to_remove / clear URLs don't carry the committed ?model= search term —
    // re-attach it so removing a facet chip doesn't silently drop the search. (The search
    // has its own chip with its own remove control.) Exception: "Clear all" (dropModel)
    // clears the search too, so strip it instead of re-attaching.
    const model = new URL(window.location.href).searchParams.get('model');
    if (model) {
      const target = new URL(url, window.location.origin);
      if (dropModel) {
        target.searchParams.delete('model');
      } else {
        target.searchParams.set('model', model);
      }
      url = target.toString();
    }

    // Desktop with an active search: reload to the target URL instead of morphing (see
    // updateFilters). For "Clear all" this navigates to the search-stripped URL for a clean full
    // render; for a facet chip it keeps ?model and re-applies the search after load.
    if (this.#shouldReloadForSearch()) {
      window.location.assign(url);
      return;
    }

    history.pushState('', '', url);
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   */
  updateFilters() {
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 * @property {HTMLInputElement} [sliderMin] - The minimum range slider thumb
 * @property {HTMLInputElement} [sliderMax] - The maximum range slider thumb
 * @property {HTMLElement} [sliderFill] - The filled track between slider thumbs
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  /** @type {string} */
  currency;
  /** @type {string} */
  moneyFormat;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
    this.currency = this.dataset.currency ?? 'USD';
    this.moneyFormat = this.#extractMoneyPlaceholder(this.dataset.moneyFormat ?? '{{amount}}');
    this.#initSlider();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  /**
   * Extracts the placeholder from a money format string, removing currency symbols.
   * @param {string} format - The money format (e.g., "${{amount}}", "{{amount}} USD")
   * @returns {string} Just the placeholder (e.g., "{{amount}}")
   */
  #extractMoneyPlaceholder(format) {
    const match = format.match(/{{\s*\w+\s*}}/);
    return match ? match[0] : '{{amount}}';
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updateSummary();
    this.#syncSliderFromInputs();
  }

  /**
   * Initialises the dual-handle range slider if present
   */
  #initSlider() {
    const { sliderMin, sliderMax } = this.refs;
    if (!sliderMin || !sliderMax) return;

    sliderMin.addEventListener('input', () => this.#onSliderMove());
    sliderMax.addEventListener('input', () => this.#onSliderMove());
    sliderMin.addEventListener('change', () => this.updatePriceFilterAndResults());
    sliderMax.addEventListener('change', () => this.updatePriceFilterAndResults());

    this.#syncSliderFromInputs();
  }

  /**
   * Handles slider thumb movement — updates fill and text inputs visually
   */
  #onSliderMove() {
    const { sliderMin, sliderMax } = this.refs;
    const min = parseFloat(sliderMin.value);
    const max = parseFloat(sliderMax.value);

    if (min > max) sliderMin.value = String(max);
    if (max < min) sliderMax.value = String(min);

    this.#updateSliderFill();
    this.#updateInputsFromSlider();
  }

  /**
   * Writes slider values into the text inputs without triggering a filter update
   */
  #updateInputsFromSlider() {
    const { sliderMin, sliderMax, minInput, maxInput } = this.refs;
    const min = parseFloat(sliderMin.value);
    const max = parseFloat(sliderMax.value);
    const rangeMax = parseFloat(sliderMax.max);

    minInput.value = min > 0 ? sliderMin.value : '';
    maxInput.value = max < rangeMax ? sliderMax.value : '';
  }

  /**
   * Reads text input values and syncs the slider thumbs to match
   */
  #syncSliderFromInputs() {
    const { sliderMin, sliderMax, minInput, maxInput } = this.refs;
    if (!sliderMin || !sliderMax) return;

    const rangeMax = parseFloat(sliderMax.max);
    const minVal = minInput.value ? parseFloat(minInput.value.replace(/[^0-9.]/g, '')) : 0;
    const maxVal = maxInput.value ? parseFloat(maxInput.value.replace(/[^0-9.]/g, '')) : rangeMax;

    sliderMin.value = String(isNaN(minVal) ? 0 : minVal);
    sliderMax.value = String(isNaN(maxVal) ? rangeMax : maxVal);

    this.#updateSliderFill();
  }

  /**
   * Updates the filled track between the two slider thumbs
   */
  #updateSliderFill() {
    const { sliderMin, sliderMax, sliderFill } = this.refs;
    if (!sliderFill) return;

    const min = parseFloat(sliderMin.value);
    const max = parseFloat(sliderMax.value);
    const rangeMax = parseFloat(sliderMax.max);

    sliderFill.style.left = `${(min / rangeMax) * 100}%`;
    sliderFill.style.right = `${((rangeMax - max) / rangeMax) * 100}%`;
  }

  /**
   * Parses a formatted money value into minor units
   * displayValue can come from user input or API response
   * @param {string} displayValue - The display value (e.g., "10.50" for USD, "9,50" for EUR, "1000" for JPY)
   * @param {string} currency - The currency code
   * @returns {number} The value in minor units
   */
  #parseDisplayValue(displayValue, currency) {
    return convertMoneyToMinorUnits(displayValue, currency) ?? 0;
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;

    const { currency, moneyFormat } = this;
    // Parse the user's input value using currency-aware parsing
    const value = this.#parseDisplayValue(input.value, currency);

    // data-min and data-max now contain raw minor unit values (not formatted)
    const min = this.#parseDisplayValue(input.getAttribute('data-min') ?? '0', currency);
    const max = this.#parseDisplayValue(input.getAttribute('data-max') ?? '0', currency);

    if (value < min) {
      input.value = formatMoney(min, moneyFormat, currency);
    } else if (value > max) {
      input.value = formatMoney(max, moneyFormat, currency);
    }
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    // "Clear all" should also remove the committed ?model= search (it has its own chip in the
    // applied-filters row), whereas removing a single facet chip keeps the search. The
    // clear-all trigger is the only one carrying a clear-all class; chips don't.
    const isClearAll = this.querySelector('.facets__clear-all-link, .facets__clear-all') !== null;

    facetsForm.updateFiltersByURL(url, isClearAll);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      const activeClass = this.getAttribute('active-class') || 'active';
      clearButton.classList.toggle(activeClass, event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;

    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    // Re-enable the input after the form-submission
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName ?? '' : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        return swatch?.outerHTML ?? '';
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    const filterStyle = this.dataset.filterStyle;

    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (filterStyle === 'horizontal' && checkedInputElementsCount === 1) {
      facetStatus.innerHTML = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const currency = facetStatus.dataset.currency || '';
    const minInputNum = this.#parseCents(minInputValue, '0', currency);
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax, currency);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as minor units (cents for most currencies, but adjusted for zero-decimal currencies)
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid (formatted string like "11,400")
   * @param {string} currency - The currency code (e.g., 'USD', 'JPY', 'KRW')
   * @returns {number} The money value in minor units
   */
  #parseCents(value, fallback = '0', currency = '') {
    // Try to parse the value
    const result = convertMoneyToMinorUnits(value, currency);
    if (result !== null) return result;

    // Fall back to parsing the fallback string (which may have formatting like "11,400")
    const fallbackResult = convertMoneyToMinorUnits(fallback, currency);
    if (fallbackResult !== null) return fallbackResult;

    // Last resort: clean and parse as integer
    const cleanFallback = fallback.replace(/[^\d]/g, '');
    return parseInt(cleanFallback, 10) || 0;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const format = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return formatMoney(moneyValue, format, currency);
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}
